import { Button, Tooltip } from "antd";
import { ApiService } from "api/api.service";
import { T_Populate } from "types/_utils.types";
import { CreditTypes } from "dtos/credit.dto";
import { FileSchemaDto } from "dtos/file.dto";
import { LoanSchemaDto } from "dtos/loan.dto";
import { useState, useRef, useEffect } from "react";
import { TbReport } from "react-icons/tb";

type Props = {
  name: string;
  email: string;
  appkey: string;
  loans?: T_Populate<
    LoanSchemaDto,
    {
      attachments: FileSchemaDto[];
    }
  >[];
};

const MAX_POLL_ATTEMPTS = 30;
const POLL_INTERVAL_MS = 5000;

const getErrorMessage = (err: unknown, fallback: string): string => {
  const anyErr = err as any;
  return anyErr?.response?.data?.message ?? anyErr?.message ?? fallback;
};

export default function RiskReport({ name, email, appkey, loans }: Props) {
  const [loading, setLoading] = useState(false);
  const timer = useRef<ReturnType<typeof setTimeout>>();
  const reportTab = useRef<Window | null>(null);
  const pollAttempts = useRef(0);

  const optionsQuery = ApiService.application.useGetDSRiskReportOptions(appkey);
  const runReport = ApiService.application.useGetDSRiskRunReport();
  const consumeCredits = ApiService.credit.useConsumeCredits();

  useEffect(() => {
    return () => {
      if (timer.current) clearTimeout(timer.current);
    };
  }, []);

  const writeToTab = (html: string) => {
    if (!reportTab.current || reportTab.current.closed) return;
    reportTab.current.document.open();
    reportTab.current.document.write(html);
    reportTab.current.document.close();
  };

  const showErrorInTab = (message: string) => {
    writeToTab(`
      <html>
        <head>
          <title>Report Error</title>
          <style>
            body {
              font-family: system-ui, -apple-system, sans-serif;
              display: flex;
              align-items: center;
              justify-content: center;
              height: 100vh;
              margin: 0;
              background: #fafafa;
            }
            .container {
              text-align: center;
              max-width: 400px;
            }
            h2 {
              color: #ff4d4f;
              font-weight: 500;
            }
            p {
              color: #555;
            }
          </style>
        </head>
        <body>
          <div class="container">
            <h2>Something went wrong</h2>
            <p>${message}</p>
          </div>
        </body>
      </html>
    `);
  };

  const showLoadingInTab = () => {
    writeToTab(`
      <html>
        <head>
          <title>Generating DS Risk+ Report</title>
          <style>
            body {
              font-family: system-ui, -apple-system, sans-serif;
              display: flex;
              align-items: center;
              justify-content: center;
              height: 100vh;
              margin: 0;
              background: #fafafa;
            }
            .container {
              text-align: center;
            }
            .spinner {
              border: 4px solid #eee;
              border-top: 4px solid #ff4d4f;
              border-radius: 50%;
              width: 40px;
              height: 40px;
              animation: spin 1s linear infinite;
              margin: 0 auto 20px auto;
            }
            @keyframes spin {
              0% { transform: rotate(0deg); }
              100% { transform: rotate(360deg); }
            }
            h2 {
              font-weight: 500;
              color: #333;
            }
          </style>
        </head>
        <body>
          <div class="container">
            <div class="spinner"></div>
            <h2>Generating DS Risk+ Report...</h2>
          </div>
        </body>
      </html>
    `);
  };

  const handleRunReport = async () => {
    if (loading) return;

    setLoading(true);
    pollAttempts.current = 0;

    reportTab.current = window.open("", "_blank");

    if (!reportTab.current) {
      // Popup blocked — nothing to write into, bail out cleanly.
      setLoading(false);
      return;
    }

    showLoadingInTab();

    try {
      const options = await optionsQuery.refetch();

      if (!options.data?.result) {
        throw new Error(getErrorMessage(options.error, "Failed to fetch report configuration."));
      }

      const body = {
        name,
        email,
        appkey,
        ruleSetIds: options.data.result.ruleSetIds,
        aiAuditReportKeys: options.data.result.aiAuditReportKeys,
        loans,
      };

      const poll = async () => {
        // Stop if the user closed the tab themselves.
        if (!reportTab.current || reportTab.current.closed) {
          setLoading(false);
          if (timer.current) clearTimeout(timer.current);
          return;
        }

        if (pollAttempts.current >= MAX_POLL_ATTEMPTS) {
          setLoading(false);
          showErrorInTab("The report is taking longer than expected. Please try again later.");
          return;
        }

        pollAttempts.current += 1;

        try {
          const res = await runReport.mutateAsync(body);

          if (typeof res.result === "object" && res.result?.redirectUrl) {
            consumeCredits.mutate({
              appkey,
              amount: 6,
              credit_type: CreditTypes.AI_AUDIT,
              description: "6 credits consumed for generating DataSense Risk+ Report.",
              extra: {
                isDSRisk: true,
              },
            });

            reportTab.current.location.href = res.result.redirectUrl;
            setLoading(false);
            return;
          }

          timer.current = setTimeout(poll, POLL_INTERVAL_MS);
        } catch (err) {
          setLoading(false);
          if (timer.current) clearTimeout(timer.current);
          showErrorInTab(getErrorMessage(err, "Unable to generate the DS Risk report. Please try again later."));
        }
      };

      poll();
    } catch (err) {
      setLoading(false);
      showErrorInTab(getErrorMessage(err, "Failed to start the DS Risk report process."));
    }
  };

  return (
    <Tooltip title="View MWV DataSense Risk Report">
      <Button onClick={handleRunReport} loading={loading} variant="solid" color="red" icon={<TbReport />}>
        DS Risk+
      </Button>
    </Tooltip>
  );
}
